import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
} from "@angular/core";
import { Router } from "@angular/router";
import { NbSpinnerService } from "@nebular/theme";
import { OKTA_AUTH } from "@okta/okta-angular";
import type OktaAuth from "@okta/okta-auth-js";

/**
 * Custom PKCE callback component. Replaces the stock
 * `OktaCallbackComponent` from `@okta/okta-angular` (which uses
 * subscription-based state propagation and requires Zone.js).
 *
 * States:
 *   - `completing` (default): spinner while handleLoginRedirect runs
 *   - `error`: actionable error screen with "Try again" + the SDK
 *     error message (clock skew, issuer mismatch, replay, network, etc.)
 *
 * We never silently redirect on failure anymore — a blank page or
 * infinite loop was the worst of both worlds for users and for support
 * ("they said it just spins").
 */
type CallbackState = "completing" | "error";

@Component({
  selector: "ngx-okta-callback",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="callback-shell">
      @if (state() === "completing") {
        <div class="panel">
          <div class="spinner" aria-hidden="true"></div>
          <p class="title">Completing sign-in…</p>
        </div>
      } @else {
        <div class="panel panel--error" role="alert">
          <p class="title">Sign-in failed</p>
          <p class="detail">{{ errorMessage() }}</p>
          <div class="actions">
            <button type="button" class="btn btn--primary" (click)="retry()">
              Try again
            </button>
            <button type="button" class="btn btn--secondary" (click)="signOutAndRetry()">
              Sign in with different account
            </button>
          </div>
          <p class="support">
            If the problem persists, contact
            <a href="mailto:support&#64;sevaro.com">support&#64;sevaro.com</a>.
          </p>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .callback-shell {
        display: flex;
        min-height: 100vh;
        align-items: center;
        justify-content: center;
        padding: 1.5rem;
        background: #f7f9fc;
      }
      .panel {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
        max-width: 28rem;
        padding: 1.75rem 2rem;
        border-radius: 0.5rem;
        border: 1px solid #edf1f7;
        background: #fff;
        box-shadow: 0 4px 16px rgba(44, 51, 73, 0.06);
        text-align: center;
      }
      .panel--error .title {
        color: #db2c66;
      }
      .title {
        font-weight: 600;
        font-size: 1rem;
        color: #222b45;
        margin: 0;
      }
      .detail {
        font-size: 0.8125rem;
        color: #5e6778;
        margin: 0;
        word-break: break-word;
      }
      .support {
        font-size: 0.75rem;
        color: #8f9bb3;
        margin: 0.5rem 0 0;
      }
      .support a {
        color: #0d62cc;
        text-decoration: none;
      }
      .support a:hover {
        text-decoration: underline;
      }
      .actions {
        margin-top: 0.5rem;
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        justify-content: center;
      }
      .btn {
        appearance: none;
        padding: 0.5rem 1rem;
        border-radius: 0.375rem;
        border: 0;
        font: inherit;
        font-weight: 500;
        cursor: pointer;
      }
      .btn--primary {
        background: #0d62cc;
        color: #fff;
      }
      .btn--primary:hover {
        background: #0b55b0;
      }
      .btn--secondary {
        background: #edf1f7;
        color: #222b45;
      }
      .btn--secondary:hover {
        background: #e4e9f2;
      }
      .spinner {
        width: 2.25rem;
        height: 2.25rem;
        border: 3px solid #e4e9f2;
        border-top-color: #0d62cc;
        border-radius: 50%;
        animation: cb-spin 0.8s linear infinite;
      }
      @keyframes cb-spin {
        to {
          transform: rotate(360deg);
        }
      }

      :host-context(body.theme-dark) .callback-shell {
        background: var(--surface-0);
      }
      :host-context(body.theme-dark) .panel {
        background: var(--surface-1);
        border-color: var(--border-subtle);
        box-shadow: var(--shadow-overlay);
      }
      :host-context(body.theme-dark) .title {
        color: var(--text-primary);
      }
      :host-context(body.theme-dark) .detail {
        color: var(--text-secondary);
      }
      :host-context(body.theme-dark) .support {
        color: var(--text-tertiary);
      }
      :host-context(body.theme-dark) .support a {
        color: var(--accent-primary);
      }
      :host-context(body.theme-dark) .btn--secondary {
        background: var(--surface-2);
        color: var(--text-primary);
      }
      :host-context(body.theme-dark) .btn--secondary:hover {
        background: var(--surface-3);
      }
      :host-context(body.theme-dark) .spinner {
        border-color: var(--border-subtle);
        border-top-color: var(--accent-primary);
      }
    `,
  ],
})
export class OktaCallbackComponent implements OnInit {
  private readonly oktaAuth = inject<OktaAuth>(OKTA_AUTH);
  private readonly router = inject(Router);
  private readonly spinnerService = inject(NbSpinnerService);
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  readonly state = signal<CallbackState>("completing");
  readonly errorMessage = signal<string>("");

  ngOnInit(): void {
    // Hide the index.html splash spinner — we render outside <nb-layout>
    // so nothing else will dismiss it.
    this.spinnerService.load();
    this.handleRedirect().catch(() => undefined);
  }

  retry(): void {
    this.state.set("completing");
    this.errorMessage.set("");
    this.oktaAuth.signInWithRedirect({ originalUri: "/admin" }).catch(() => undefined);
  }

  async signOutAndRetry(): Promise<void> {
    this.state.set("completing");
    this.errorMessage.set("");
    try {
      await this.oktaAuth.tokenManager.clear();
      await this.oktaAuth.signOut({
        postLogoutRedirectUri: window.location.origin + "/login/callback",
      });
    } catch {
      await this.oktaAuth.signInWithRedirect({ originalUri: "/admin" });
    }
  }

  private showError(message: string): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.errorMessage.set(message);
    this.state.set("error");
  }

  private async handleRedirect(): Promise<void> {
    // Fail-open timeout: if Okta hangs (wrong tenant, blocked third-party
    // cookies, network stall) we surface the error UI instead of spinning
    // forever. User can pick "Sign in with different account" to clear
    // the stale session.
    this.timeoutId = setTimeout(() => {
      if (this.state() === "completing") {
        this.showError(
          "Sign-in is taking longer than expected. This usually means the session is stuck on a different account. Try signing in with a different account.",
        );
      }
    }, 15000);

    try {
      if (this.oktaAuth.isLoginRedirect()) {
        await this.oktaAuth.handleLoginRedirect();
      }
      const authed = await this.oktaAuth.isAuthenticated();
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
      if (authed) {
        await this.router.navigateByUrl("/admin");
      } else {
        // Do NOT auto-redirect to signInWithRedirect here. If the user
        // just failed auth because the browser is holding a session for
        // the wrong tenant/account, looping back to Okta produces the
        // same outcome and feels like the app is stuck. Show an explicit
        // error with a "different account" escape hatch.
        this.showError(
          "We couldn't complete sign-in. If you're signed in to a different Okta account, choose 'Sign in with different account' below.",
        );
      }
    } catch (err: unknown) {
      this.showError(
        err instanceof Error && err.message
          ? err.message
          : "Unexpected error completing sign-in.",
      );
    }
  }
}
